/**
 * Aufklapp-Menue fuer die Kopfleiste (Rafael, 03.10.2026: „die Leisten ganz oben sind
 * chaotisch … so wirklich optimiert ist der obere Teil ja immer noch nicht").
 *
 * Was selten gebraucht wird (zwoelf KI-Messbalken, Rebuild, Cache, Sync, zweiter
 * Bildschirm, Standardaufteilung), steht hier statt dauerhaft in der Leiste. Das
 * Menue haengt per Portal an <body>, damit es weder von der Leiste noch von einer
 * Guide-Spalte abgeschnitten wird; die Lage folgt dem Knopf (auch bei CSS-zoom,
 * siehe ViewControl).
 */
import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';

export default function KopfMenue({ knopf, knopfTitel, aiId, menueTitel, breite = 360, knopfStil, klasse, children }: {
  knopf: ReactNode;
  knopfTitel: string;
  aiId: string;
  menueTitel: string;
  breite?: number;
  knopfStil?: React.CSSProperties;
  klasse?: string;
  children: ReactNode | ((schliessen: () => void) => ReactNode);
}) {
  const [offen, setOffen] = useState(false);
  const knopfRef = useRef<HTMLButtonElement>(null);
  const menue = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    if (!offen) return;
    let frame = 0;
    const lage = () => {
      if (knopfRef.current && menue.current) {
        const zoom = Number.parseFloat(getComputedStyle(document.documentElement).zoom) || 1;
        const a = knopfRef.current.getBoundingClientRect();
        const w = window.innerWidth / zoom;
        const b = Math.min(breite, w - 16);
        const top = a.bottom / zoom + 6;
        menue.current.style.width = `${b}px`;
        menue.current.style.left = `${Math.max(8, Math.min(a.left / zoom, w - b - 8))}px`;
        menue.current.style.top = `${top}px`;
        menue.current.style.maxHeight = `${Math.max(120, window.innerHeight / zoom - top - 8)}px`;
      }
      frame = requestAnimationFrame(lage);
    };
    lage();
    return () => cancelAnimationFrame(frame);
  }, [offen, breite]);
  useEffect(() => {
    if (!offen) return;
    const weg = (e: PointerEvent) => {
      if (!menue.current?.contains(e.target as Node) && !knopfRef.current?.contains(e.target as Node)) setOffen(false);
    };
    const esc = (e: KeyboardEvent) => { if (e.key === 'Escape') { setOffen(false); knopfRef.current?.focus(); } };
    document.addEventListener('pointerdown', weg); document.addEventListener('keydown', esc);
    return () => { document.removeEventListener('pointerdown', weg); document.removeEventListener('keydown', esc); };
  }, [offen]);
  const schliessen = () => setOffen(false);
  return <>
    <button ref={knopfRef} type="button" className={`cui-kopf-menue-knopf ${klasse ?? ''}`} data-ai-id={aiId}
      aria-expanded={offen} aria-haspopup="dialog" title={knopfTitel} onClick={() => setOffen(o => !o)}
      style={{ WebkitAppRegion: 'no-drag', ...knopfStil } as React.CSSProperties}>
      {knopf}
    </button>
    {offen && createPortal(
      <div ref={menue} className="cui-kopf-menue" role="dialog" aria-label={menueTitel} data-ai-id={`${aiId}-inhalt`}>
        <div className="cui-view-heading">{menueTitel}
          <button type="button" aria-label="Menü schließen" onClick={() => { setOffen(false); knopfRef.current?.focus(); }}>×</button>
        </div>
        {typeof children === 'function' ? children(schliessen) : children}
      </div>, document.body)}
  </>;
}
